import React, { useCallback, useState } from 'react';
import { Alert, Button } from 'antd';
import PageTabs from '../page/PageTabs';
import RegulatoryItemsPanel from './RegulatoryItemsPanel';
import RegulatoryReviewQueue from './RegulatoryReviewQueue';
import RegulatoryCoverage from './RegulatoryCoverage';
import { topicTitleMap } from '../../utils/regulatoryAdminModel';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';

const QUEUE_PAGE = 20;

/**
 * Regulatory Database console (platform_admin only; the page gates before mounting, so no request is made for
 * any other persona). Vocabularies, limits and break-glass availability come from GET /regulatory-content/sources.
 */
const RegulatoryAdminConsole = () => {
    const { request } = useApiRequest();
    const [rev, setRev] = useState(0);
    const [queueOffset, setQueueOffset] = useState(0);
    const [activeKey, setActiveKey] = useState('documents');
    const [openId, setOpenId] = useState(null);

    const sources = useResource(async () => (await request('GET', '/regulatory-content/sources')).data, [request]);
    const queue = useResource(
        async () => {
            const { data, meta } = await request('GET', '/regulatory-content/enrichments', { query: { pending: 'true', limit: QUEUE_PAGE, offset: queueOffset } });
            return { rows: data?.enrichments || [], total: meta?.total ?? (data?.enrichments || []).length, hasMore: data?.hasMore, nextOffset: data?.nextOffset };
        },
        [request, rev, queueOffset],
    );
    const onChanged = useCallback(async () => { setRev((r) => r + 1); }, []);

    const queueView = { ...queue, data: queue.data ? queue.data.rows : null };
    const pending = queue.data?.total ?? 0;
    const src = sources.data;

    return (
        <div>
            {sources.status === 'error' && (
                <Alert
                    type="error"
                    style={{ marginBottom: 12 }}
                    message={userMessageForError(sources.error)}
                    action={<Button onClick={sources.reload}>Retry</Button>}
                />
            )}
            <PageTabs
                activeKey={activeKey}
                onChange={setActiveKey}
                items={[
                    { key: 'documents', label: 'Documents', children: <RegulatoryItemsPanel sources={src} rev={rev} onChanged={onChanged} openId={openId} /> },
                    {
                        key: 'review',
                        label: 'Review queue',
                        count: pending > 0 ? pending : undefined,
                        children: <RegulatoryReviewQueue queue={queueView} total={pending} offset={queueOffset} pageSize={QUEUE_PAGE} hasMore={queue.data?.hasMore} nextOffset={queue.data?.nextOffset} topicTitles={topicTitleMap(src?.taxonomy)} onPage={setQueueOffset} breakGlassAvailable={src?.breakGlassAvailable === true} onChanged={onChanged} onOpenItem={(id) => { setOpenId(id); setActiveKey('documents'); }} />,
                    },
                    { key: 'coverage', label: 'Coverage', children: <RegulatoryCoverage /> },
                ]}
            />
        </div>
    );
};

export default RegulatoryAdminConsole;
